import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import BodyContainer from "../components/BodyContainer";
import BottomContainer from "../components/BottomContainer";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet-async";
import "../TestPage.css";
import WatermarkOverlay from "../components/WatermarkOverlay";
import "../Watermark.css";

import { InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

import LegalWarningModal from "../components/LegalWarningModal";

function renderTextWithLatex(text) {
  if (!text) return null;

  const parts = text.split(/(\$[^$]+\$)/g);

  return parts.map((part, idx) => {
    if (part.startsWith("$") && part.endsWith("$")) {
      return <InlineMath key={idx} math={part.slice(1, -1)} />;
    }

    return <span key={idx}>{part}</span>;
  });
}

function QuestionBankPage() {
  const { bankId } = useParams();

  const navigate = useNavigate();
  const location = useLocation();

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const user = JSON.parse(localStorage.getItem("user"));

  const [bank, setBank] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [score, setScore] = useState(0);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const [selectedAnswer, setSelectedAnswer] = useState(false);

  const [showIntro, setShowIntro] = useState(true);

  const [showWarning, setShowWarning] = useState(false);

  const [fillInValue, setFillInValue] = useState("");

  // Questions passed from QuestionBank.js
  const passedQuestions = location.state?.questions || [];

  // 🔒 Security logging
  const triggerWarning = async (reason) => {
    setShowWarning(true);

    try {
      await fetch(`${API_URL}/api/security/log`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user?.Id,
          reason,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.error("Security log failed", err);
    }
  };

  useEffect(() => {
  setFillInValue("");
}, [currentQuestionIndex]);

  // 🔒 Screenshot protection
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();

      const isScreenshot =
        ["printscreen", "prtsc", "prtscr", "prtscn"].includes(key) ||
        ((e.ctrlKey || e.metaKey) && key === "p") ||
        ((e.ctrlKey || e.metaKey) &&
          e.shiftKey &&
          key === "s") ||
        (e.metaKey &&
          e.shiftKey &&
          ["3", "4"].includes(key));

      const isCopyOrSave =
        (e.ctrlKey || e.metaKey) &&
        ["c", "s"].includes(key);

      if (isScreenshot) {
        e.preventDefault();
        triggerWarning("screenshot_attempt");
      } else if (isCopyOrSave) {
        e.preventDefault();
        triggerWarning(`copy_or_save_${key}`);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, []);

  // ---------------- FETCH BANK ----------------
  useEffect(() => {
    async function fetchBank() {
      try {
        const res = await fetch(
          `${API_URL}/api/questionbanks/${bankId}`
        );

        if (!res.ok) {
          throw new Error("Failed to load question bank");
        }

        const data = await res.json();

        let questions = passedQuestions;

        // fallback if no passed questions
        if (!questions.length) {
          questions = [...(data.Questions || [])];
        }

        // shuffle
        questions = [...questions].sort(
          () => Math.random() - 0.5
        );

        setBank({
          ...data,
          Questions: questions,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchBank();
  }, [bankId]);

  // ---------------- ANSWERS ----------------
  const handleAnswerChange = (value, type) => {
    setAnswers((prev) => {
      if (type === "Select All") {
        const prevAnswers =
          prev[currentQuestionIndex] || [];

        return {
          ...prev,
          [currentQuestionIndex]:
            prevAnswers.includes(value)
              ? prevAnswers.filter(
                  (ans) => ans !== value
                )
              : [...prevAnswers, value],
        };
      }

      return {
        ...prev,
        [currentQuestionIndex]: value,
      };
    });

    if (type === "Select All") {
      setSelectedAnswer(true);
    } else if (type === "Fill In") {
      setSelectedAnswer(value.trim() !== "");
    } else {
      setSelectedAnswer(!!value);
    }
  };

  // ---------------- NEXT QUESTION ----------------
  const handleNextQuestion = () => {
    setSelectedAnswer(false);

    setCurrentQuestionIndex(
      (prev) => prev + 1
    );
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = () => {
    let totalCorrect = 0;

    bank.Questions.forEach((question, index) => {
      const userAnswer = answers[index] || "";

      let isCorrect = false;

      if (question.Type === "Select All") {
        const correctAnswers = question.CorrectAnswer
          .map((a) => a.trim())
          .sort()
          .join(",");

        const userAnswers = (
          Array.isArray(userAnswer)
            ? userAnswer.map((a) => a.trim())
            : []
        )
          .sort()
          .join(",");

        isCorrect =
          correctAnswers === userAnswers;
      } else if (question.Type === "Fill In") {
        const correctAnswer = Array.isArray(
          question.CorrectAnswer
        )
          ? question.CorrectAnswer.map((a) =>
              a.toLowerCase().trim()
            )
          : [
              question.CorrectAnswer
                .toLowerCase()
                .trim(),
            ];

        isCorrect = correctAnswer.includes(
          userAnswer.toLowerCase().trim()
        );
      } else {
        isCorrect =
          userAnswer === question.CorrectAnswer;
      }

      if (isCorrect) {
        totalCorrect++;
      }
    });

    setScore(totalCorrect);

    setSubmitted(true);
  };

  // ---------------- REVIEW ANSWER TEXT ----------------
  const getAnswerText = (
    answer,
    question,
    type
  ) => {
    if (
      type === "Multiple Choice" ||
      type === "Select All"
    ) {
      if (Array.isArray(answer)) {
        return answer
          .map((ans) => {
            const option =
              question.Options?.[ans];

            return option
              ? `${ans}: ${option}`
              : null;
          })
          .filter(Boolean)
          .join(", ");
      }

      return question.Options?.[answer]
        ? `${answer}: ${question.Options[answer]}`
        : "No Answer";
    }

    return answer || "No Answer";
  };

  return (
    <>
      <Helmet>
        <title>
          PharmPrepPro -{" "}
          {bank?.Name || "Question Bank"}
        </title>
      </Helmet>

      <BottomContainer>
        <BodyContainer>
          {loading ? (
            <p className="test-loading">
              Loading question bank...
            </p>
          ) : error ? (
            <p className="test-error">{error}</p>
          ) : !bank ? (
            <p className="test-error">
              Question bank not found.
            </p>
          ) : showIntro ? (
            <div className="test-intro-container">
              <h1 className="test-title">
                {bank.Name}
              </h1>

              <p className="test-description">
                {bank.Description}
              </p>

              <p>
                Questions:{" "}
                {bank.Questions?.length || 0}
              </p>

              <button
                className="start-test-button"
                onClick={() => setShowIntro(false)}
              >
                Start Practice
              </button>
            </div>
          ) : (
            <div className="test-container">
              <h1 className="test-title">
                {bank.Name}
              </h1>

              {!submitted ? (
                <div className="question-card">
                  {(bank.Questions[
                    currentQuestionIndex
                  ].PreSignedImageURL ||
                    bank.Questions[
                      currentQuestionIndex
                    ].Image) && (
                    <div className="question-image">
                      <img
                        src={
                          bank.Questions[
                            currentQuestionIndex
                          ].PreSignedImageURL ||
                          `${process.env.REACT_APP_S3_BASE_URL}/${bank.Questions[currentQuestionIndex].Image}`
                        }
                        alt="Question"
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          margin: "10px 0",
                        }}
                      />
                    </div>
                  )}

                  <p className="question-text">
                    {currentQuestionIndex + 1}.{" "}
                    {renderTextWithLatex(
                      bank.Questions[
                        currentQuestionIndex
                      ].Question
                    )}
                  </p>

                  {[
                    "Multiple Choice",
                    "Select All",
                  ].includes(
                    bank.Questions[
                      currentQuestionIndex
                    ].Type
                  ) ? (
                    <div className="question-options">
                      {Object.entries(
                        bank.Questions[
                          currentQuestionIndex
                        ].Options
                      ).map(([key, value]) => (
                        <label
                          key={key}
                          className="option-label"
                        >
                          <input
                            type={
                              bank.Questions[
                                currentQuestionIndex
                              ].Type ===
                              "Multiple Choice"
                                ? "radio"
                                : "checkbox"
                            }
                            name={`question-${currentQuestionIndex}`}
                            value={key}
                            checked={Array.isArray(
                              answers[
                                currentQuestionIndex
                              ]
                            )
                              ? answers[
                                  currentQuestionIndex
                                ].includes(key)
                              : answers[
                                  currentQuestionIndex
                                ] === key}
                            onChange={(e) =>
                              handleAnswerChange(
                                e.target.value,
                                bank.Questions[
                                  currentQuestionIndex
                                ].Type
                              )
                            }
                          />

                          {renderTextWithLatex(
                            value
                          )}
                        </label>
                      ))}
                    </div>
                  ) : (
                  <input
                    type="text"
                    className="fill-blank-input"
                    placeholder="Your answer..."
                    value={fillInValue}
                    onChange={(e) => {
                      setFillInValue(e.target.value);
                      handleAnswerChange(e.target.value, "Fill In");
                    }}
                  />
                  )}

                  <div className="navigation-buttons">
                    {currentQuestionIndex <
                    bank.Questions.length - 1 ? (
                      <button
                        className={`next-button ${
                          !selectedAnswer
                            ? "disabled"
                            : ""
                        }`}
                        disabled={!selectedAnswer}
                        onClick={
                          handleNextQuestion
                        }
                      >
                        Next Question
                      </button>
                    ) : (
                      <button
                        className="submit-button"
                        onClick={handleSubmit}
                      >
                        Submit Practice
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="test-results">
                  <WatermarkOverlay user={user} />

                  <h2>Results</h2>

                  <p className="score-text">
                    Your Score: {score} /{" "}
                    {bank.Questions.length}
                  </p>

                  <h3>Review Answers</h3>

                  <ul className="review-list">
                    {bank.Questions.map(
                      (question, index) => {
                        const userAnswer =
                          answers[index];

                        const correctAnswer =
                          question.CorrectAnswer;

                        const imageUrl =
                          question.PreSignedImageURL ||
                          (question.Image
                            ? `${process.env.REACT_APP_S3_BASE_URL}/${question.Image}`
                            : null);

                        return (
                          <li
                            key={index}
                            className={`review-item ${
                              userAnswer?.toString() ===
                              correctAnswer?.toString()
                                ? "correct"
                                : "incorrect"
                            }`}
                          >
                            <p>
                              <strong>
                                Q{index + 1}:
                              </strong>{" "}
                              {renderTextWithLatex(
                                question.Question
                              )}
                            </p>

                            {imageUrl && (
                              <div className="question-image">
                                <img
                                  src={imageUrl}
                                  alt={`Question ${index + 1}`}
                                  style={{
                                    maxWidth:
                                      "100%",
                                    height:
                                      "auto",
                                    margin:
                                      "10px 0",
                                    borderRadius:
                                      "8px",
                                  }}
                                />
                              </div>
                            )}

                            <p>
                              <strong>
                                Your Answer:
                              </strong>{" "}
                              {renderTextWithLatex(
                                getAnswerText(
                                  userAnswer,
                                  question,
                                  question.Type
                                )
                              )}
                            </p>

                            <p>
                              <strong>
                                Correct Answer:
                              </strong>{" "}
                              {renderTextWithLatex(
                                getAnswerText(
                                  correctAnswer,
                                  question,
                                  question.Type
                                )
                              )}
                            </p>

                            <p className="explanation">
                              <strong>
                                Explanation:
                              </strong>{" "}
                              {renderTextWithLatex(
                                question.Explanation
                              )}
                            </p>
                          </li>
                        );
                      }
                    )}
                  </ul>

                  <button
                    className="back-button"
                    onClick={() =>
                      navigate("/practicequestions")
                    }
                  >
                    Back to Practice Questions
                  </button>
                </div>
              )}
            </div>
          )}
        </BodyContainer>

        <Footer />
      </BottomContainer>

      <LegalWarningModal
        show={showWarning}
        onClose={() => setShowWarning(false)}
      />
    </>
  );
}

export default QuestionBankPage;